import React, { useCallback, useEffect, useState } from 'react';
import { showTimedToast } from '../utils/toast';
import {
    createSpecFileListValue,
    isFirmwareSpecTitle,
    isImageSpecTitle,
    isUploadSpecTitle,
    isWiringDiagramSpecTitle,
    parseSpecFileListValue,
} from '../utils/specFiles';
import {
    ADJUSTER_INITIAL_SPEC_ROWS,
    CONTROLLER_INITIAL_SPEC_ROWS,
    toSpecTitleByType,
} from '../constants/specTitles';

const getMinNormalSpecRowsForType = (type) => (
    type === 'controller' ? CONTROLLER_INITIAL_SPEC_ROWS : ADJUSTER_INITIAL_SPEC_ROWS
);
import { SIDO_OPTIONS, getGugunOptions } from '../constants/koreaRegions';
import SpecDropdownSelect from './SpecDropdownSelect';
import AppleSwitchCheckbox from './AppleSwitchCheckbox';
import { getApiBaseUrl } from '../apiBase';

const ITEMS_PER_PAGE = 25;
const JSON_HEADERS = {
    'Content-Type': 'application/json'
};

const formatRejectHistoryInlineText = (model) => {
    const list = Array.isArray(model?.reviewRejectHistory) ? model.reviewRejectHistory : [];
    if (list.length === 0) {
        return '';
    }
    return list
        .map((entry, idx) => `승인 ${idx + 1}차 거부 내용:${String(entry?.reason || '').trim() || '-'}`)
        .join(', ');
};
const normalizeSpecsByType = (type, specs = []) => {
    let nonFullIndex = 0;
    return specs.map((spec) => {
        if (spec.fullWidth) {
            return { ...spec };
        }
        const next = {
            ...spec,
            title: toSpecTitleByType(type, nonFullIndex),
        };
        nonFullIndex += 1;
        return next;
    });
};

const SpecList = ({
    models = [],
    onUpdateModel,
    onDeleteModel,
    onApproveModel,
    onRejectModel,
    canEditProduct = false,
    canViewDetail = true,
    canManagePermissions = false,
    canDownloadFirmware = true,
    canDownloadWiringDiagram = false,
    showTypeFilter = false,
    typeFilters = { adjuster: true, controller: true },
    onToggleTypeFilter,
    editRequest = null,
    onEditRequestHandled,
    currentUserName = '',
    allowPendingExpand = false,
    showReviewMeta = false,
    headerExtra = null,
    renderCollapsedExtra,
    specDropdownOptions = [],
    expandFromMapRequest = null,
    onExpandFromMapHandled,
    enableRowSelect = false,
    selectedRowIds = [],
    onToggleRowSelect,
    isRowSelectable,
    /** true면 접힌 목록 행에 모델명만 표시(현장명·세대수 숨김) */
    listSummaryModelNameOnly = false,
    /** 메인 리스트 vs 모델별 사양 리스트 UI 구분 */
    listVariant = 'mainList',
    /** 모델별 전용: 카드 덱 형태 목록 */
    presentationVariant = 'default',
    /** 섹션 상단 제목(h3) 숨김 — 외부 패널에서 제목을 쓸 때 */
    omitSectionHeading = false,
}) => {
    const [expandedModelId, setExpandedModelId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [editingModelId, setEditingModelId] = useState(null);
    const [editingDraft, setEditingDraft] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastVisible, setToastVisible] = useState(false);
    const [toastType, setToastType] = useState('error');
    const [lightboxImage, setLightboxImage] = useState(null);
    const [pendingScrollModelId, setPendingScrollModelId] = useState(null);
    const getReviewStatusNorm = (model) => String(model?.reviewStatus ?? 'approved').trim().toLowerCase();
    const isApprovalRejectedList = (model) => getReviewStatusNorm(model) === 'rejected';
    const hasReviewRejectHistory = (model) => Array.isArray(model?.reviewRejectHistory) && model.reviewRejectHistory.length > 0;
    /** 승인 거부 상태(rejected)일 때만 작성자 제한 */
    const isPostRejectAuthorOnlyList = (model) => getReviewStatusNorm(model) === 'rejected';
    /** 신규등록 거부(rejected) 또는 수정·삭제 요청 거부 후 이력만 남은 경우(approved + history) */
    const showRejectDetailUnderMemo = (model) => (
        isApprovalRejectedList(model)
        || (getReviewStatusNorm(model) === 'approved' && hasReviewRejectHistory(model))
    );
    const showGeneralListReviewBadge = (model) => {
        if (allowPendingExpand) {
            return false;
        }
        const rs = getReviewStatusNorm(model);
        return rs === 'pending' || rs === 'rejected';
    };
    const isOwnerModel = (model) => (
        String(model?.pendingRequesterName || '').trim()
        && String(model?.pendingRequesterName || '').trim() === String(currentUserName || '').trim()
    );
    const hasPermissionManagerBypass = canManagePermissions;
    /** 일반 목록에서 세부 열람 제한(잠금 표시 등)에 쓰임 — pending은 전원, 거부는 비작성자 */
    const isMainListDetailLockedForUser = (model) => {
        if (allowPendingExpand) {
            return false;
        }
        if (hasPermissionManagerBypass) {
            return false;
        }
        if (getReviewStatusNorm(model) === 'pending') {
            return true;
        }
        return isPostRejectAuthorOnlyList(model) && !isOwnerModel(model);
    };

    const totalPages = Math.max(1, Math.ceil(models.length / ITEMS_PER_PAGE));
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const pagedModels = models.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    const showToast = useCallback((message, type = 'error') => {
        setToastType(type);
        showTimedToast({
            setMessage: setToastMessage,
            setVisible: setToastVisible,
            message,
        });
    }, []);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
            setExpandedModelId(null);
        }
    }, [currentPage, totalPages]);

    useEffect(() => {
        if (!editRequest || !editRequest.key || !canEditProduct || editingModelId) {
            return;
        }
        const matchById = editRequest.id
            ? models.find((model) => Number(model.id) === Number(editRequest.id))
            : null;
        const match = matchById || models.find((model) => (
            String(model.siteName || '').trim() === String(editRequest.siteName || '').trim()
            && String(model.name || '').trim() === String(editRequest.productName || '').trim()
            && (!editRequest.type || String(model.type || '').trim() === String(editRequest.type || '').trim())
        ));
        if (match) {
            const matchIndex = models.findIndex((model) => model.id === match.id);
            if (matchIndex >= 0) {
                const targetPage = Math.floor(matchIndex / ITEMS_PER_PAGE) + 1;
                if (targetPage !== currentPage) {
                    setCurrentPage(targetPage);
                }
            }
            startEdit(match);
            setPendingScrollModelId(match.id);
            if (typeof onEditRequestHandled === 'function') {
                onEditRequestHandled(editRequest.key);
            }
        }
    }, [editRequest, models, canEditProduct, editingModelId, currentPage]);

    useEffect(() => {
        if (!pendingScrollModelId) {
            return;
        }
        if (!pagedModels.some((model) => model.id === pendingScrollModelId)) {
            return;
        }
        const raf = window.requestAnimationFrame(() => {
            const targetEl = document.getElementById(`model-row-${pendingScrollModelId}`);
            if (targetEl) {
                targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            setPendingScrollModelId(null);
        });
        return () => window.cancelAnimationFrame(raf);
    }, [pendingScrollModelId, pagedModels]);

    const writeClientLog = async ({ actionType, targetType, targetId, description, metadata }) => {
        let userName = currentUserName;

        if (!userName) {
            try {
                const raw = localStorage.getItem('bns-user');
                if (raw) {
                    const parsed = JSON.parse(raw);
                    userName = parsed?.name || '';
                }
            } catch (e) {
                // ignore
            }
        }

        if (!userName) {
            return;
        }

        try {
            const res = await fetch(`${getApiBaseUrl()}/logs/event`, {
                method: 'POST',
                headers: JSON_HEADERS,
                body: JSON.stringify({
                    requesterName: userName,
                    actionType,
                    targetType,
                    targetId,
                    description,
                    metadata,
                })
            });
            if (!res.ok) {
                console.error('[SpecList] 로그 저장 실패:', res.status, actionType);
            }
        } catch (error) {
            console.error('[SpecList] 로그 fetch 오류:', error);
        }
    };

    useEffect(() => {
        if (!expandFromMapRequest?.key || expandFromMapRequest.modelId == null) {
            return;
        }
        const modelId = expandFromMapRequest.modelId;
        const idx = models.findIndex((m) => Number(m.id) === Number(modelId));
        const finish = () => {
            if (typeof onExpandFromMapHandled === 'function') {
                onExpandFromMapHandled();
            }
        };
        if (idx < 0) {
            finish();
            return;
        }
        if (editingModelId && editingModelId !== modelId) {
            finish();
            return;
        }
        if (editingModelId && editingModelId === modelId) {
            setExpandedModelId(modelId);
            setPendingScrollModelId(modelId);
            finish();
            return;
        }
        const selectedModel = models.find((item) => item.id === modelId);
        if (!allowPendingExpand && !hasPermissionManagerBypass && getReviewStatusNorm(selectedModel) === 'pending') {
            showToast('승인 검토중에는 세부사항을 열람할 수 없습니다.', 'error');
            finish();
            return;
        }
        if (!allowPendingExpand && !hasPermissionManagerBypass && isPostRejectAuthorOnlyList(selectedModel) && !isOwnerModel(selectedModel)) {
            showToast('승인 거부된 리스트는 작성자만 세부사항을 열람할 수 있습니다.', 'error');
            finish();
            return;
        }
        if (!isPostRejectAuthorOnlyList(selectedModel) && !canViewDetail) {
            finish();
            return;
        }
        const targetPage = Math.floor(idx / ITEMS_PER_PAGE) + 1;
        setCurrentPage(targetPage);
        setExpandedModelId(modelId);
        writeClientLog({
            actionType: 'list_detail_view',
            targetType: 'product',
            targetId: modelId,
            description: '세부사항 확인',
            metadata: {
                siteName: selectedModel?.siteName || '',
                productName: selectedModel?.name || '',
            },
        });
        setPendingScrollModelId(modelId);
        finish();
    }, [expandFromMapRequest, models, editingModelId, canViewDetail, allowPendingExpand, onExpandFromMapHandled, currentUserName, showToast, hasPermissionManagerBypass]);

    useEffect(() => {
        if (expandedModelId == null) {
            return;
        }
        const open = models.find((item) => item.id === expandedModelId);
        if (!open || editingModelId === expandedModelId) {
            return;
        }
        if (!allowPendingExpand && !hasPermissionManagerBypass && getReviewStatusNorm(open) === 'pending') {
            setExpandedModelId(null);
            return;
        }
        if (!allowPendingExpand && !hasPermissionManagerBypass && isPostRejectAuthorOnlyList(open) && !isOwnerModel(open)) {
            setExpandedModelId(null);
        }
    }, [models, expandedModelId, editingModelId, allowPendingExpand, currentUserName, hasPermissionManagerBypass]);

    const toggleExpand = (modelId) => {
        if (editingModelId && editingModelId !== modelId) {
            showToast('리스트 수정 중에는 다른 리스트를 선택할 수 없습니다.', 'error');
            return;
        }
        if (editingModelId && editingModelId === modelId) {
            return;
        }

        const selectedModel = models.find((item) => item.id === modelId);
        if (!allowPendingExpand && !hasPermissionManagerBypass && getReviewStatusNorm(selectedModel) === 'pending') {
            showToast('승인 검토중에는 세부사항을 열람할 수 없습니다.', 'error');
            return;
        }
        if (!allowPendingExpand && !hasPermissionManagerBypass && isPostRejectAuthorOnlyList(selectedModel) && !isOwnerModel(selectedModel)) {
            showToast('승인 거부된 리스트는 작성자만 세부사항을 열람할 수 있습니다.', 'error');
            return;
        }
        if (!isPostRejectAuthorOnlyList(selectedModel) && !canViewDetail) {
            showToast('세부사항 열람 권한이 없습니다 관리자에게 문의 바랍니다.', 'error');
            return;
        }

        const nextExpanded = expandedModelId === modelId ? null : modelId;
        setExpandedModelId(nextExpanded);

        if (nextExpanded) {
            const model = models.find((item) => item.id === modelId);
            writeClientLog({
                actionType: 'list_detail_view',
                targetType: 'product',
                targetId: modelId,
                description: '세부사항 확인',
                metadata: {
                    siteName: model?.siteName || '',
                    productName: model?.name || '',
                },
            });
        }
    };

    const startEdit = (model) => {
        if (!canEditProduct) {
            showToast('리스트 수정 권한이 없습니다.', 'error');
            return;
        }
        if (!hasPermissionManagerBypass && model?.reviewStatus === 'pending') {
            showToast('검토 승인중인 항목은 수정할 수 없습니다.', 'error');
            return;
        }
        if (!hasPermissionManagerBypass && isPostRejectAuthorOnlyList(model) && !isOwnerModel(model)) {
            showToast('승인 거부된 리스트는 작성자만 수정할 수 있습니다.', 'error');
            return;
        }

        setEditingModelId(model.id);
        setExpandedModelId(model.id);
        const nextSpecs = normalizeSpecsByType(model.type, model.specs || []);
        setEditingDraft({
            id: model.id,
            siteName: model.siteName,
            name: model.name,
            householdCount: model.householdCount || '',
            type: model.type,
            sido: model.sido || '',
            gugun: model.gugun || '',
            dong: model.dong || '',
            detailAddress: model.detailAddress || '',
            specs: nextSpecs,
        });
    };

    const cancelEdit = () => {
        setEditingModelId(null);
        setEditingDraft(null);
        setIsSaving(false);
        setIsDeleting(false);
    };

    const saveEdit = async () => {
        if (!editingDraft || !onUpdateModel) {
            return;
        }

        setIsSaving(true);
        const ok = await onUpdateModel(editingDraft);
        setIsSaving(false);

        if (ok) {
            cancelEdit();
            setExpandedModelId(null);
            showToast('수정이 완료되었습니다.', 'success');
        } else {
            showToast('수정 저장에 실패했습니다.', 'error');
        }
    };

    const deleteModel = async (modelId) => {
        if (!onDeleteModel) {
            return;
        }

        const okToDelete = window.confirm('삭제 요청을 올리시겠습니까? 승인 완료 후 실제 삭제됩니다.');
        if (!okToDelete) {
            return;
        }

        setIsDeleting(true);
        const ok = await onDeleteModel(modelId);
        setIsDeleting(false);

        if (ok) {
            cancelEdit();
            setExpandedModelId(null);
            showToast('삭제 요청이 등록되었습니다. 승인 후 삭제됩니다.', 'success');
        } else {
            showToast('삭제 요청에 실패했습니다.', 'error');
        }
    };

    const updateDraftField = (field, value) => {
        setEditingDraft((prev) => ({ ...prev, [field]: value }));
    };

    const updateDraftSpec = (specIndex, value) => {
        setEditingDraft((prev) => {
            const nextSpecs = [...prev.specs];
            nextSpecs[specIndex] = { ...nextSpecs[specIndex], details: value };
            return { ...prev, specs: nextSpecs };
        });
    };

    const updateDraftSpecFile = (specIndex, kind) => (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) {
            return;
        }

        if (kind === 'firmware' && files.some((file) => !file.name.toLowerCase().endsWith('.hex'))) {
            showToast('.HEX 파일만 업로드할 수 있습니다.', 'error');
            e.target.value = '';
            return;
        }

        if (kind === 'wiringPdf' && files.some((file) => {
            const lower = file.name.toLowerCase();
            const mime = String(file.type || '').toLowerCase();
            return !lower.endsWith('.pdf') && !mime.includes('pdf');
        })) {
            showToast('PDF 파일만 업로드할 수 있습니다.', 'error');
            e.target.value = '';
            return;
        }

        const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result || ''));
            reader.onerror = () => reject(new Error('파일 읽기 실패'));
            reader.readAsDataURL(file);
        });

        Promise.all(files.map(async (file) => ({
            kind,
            name: file.name,
            mimeType: file.type,
            dataUrl: await readFileAsDataUrl(file),
            description: '',
        })))
            .then((nextFiles) => {
                const existing = parseSpecFileListValue(editingDraft?.specs?.[specIndex]?.details);
                updateDraftSpec(specIndex, createSpecFileListValue({ files: [...existing, ...nextFiles] }));
            })
            .catch(() => {
                showToast('파일을 읽는 중 오류가 발생했습니다.', 'error');
            });
        e.target.value = '';
    };

    const clearDraftSpecFile = (specIndex, fileIndex) => {
        const existing = parseSpecFileListValue(editingDraft?.specs?.[specIndex]?.details);
        const next = existing.filter((_, idx) => idx !== fileIndex);
        updateDraftSpec(specIndex, next.length > 0 ? createSpecFileListValue({ files: next }) : '');
    };

    const updateDraftUploadFileDescription = (specIndex, fileIndex, description) => {
        const existing = parseSpecFileListValue(editingDraft?.specs?.[specIndex]?.details);
        const target = existing[fileIndex];
        if (!target || (target.kind !== 'firmware' && target.kind !== 'wiringPdf')) {
            return;
        }
        const next = existing.map((file, idx) => (idx === fileIndex ? { ...file, description } : file));
        updateDraftSpec(specIndex, createSpecFileListValue({ files: next }));
    };

    const addDraftSpec = () => {
        setEditingDraft((prev) => {
            if (!prev) {
                return prev;
            }

            const firstFullWidthIndex = prev.specs.findIndex((spec) => spec.fullWidth);
            const specCount = prev.specs.filter((spec) => !spec.fullWidth).length;
            const newSpec = {
                title: toSpecTitleByType(prev.type, specCount),
                details: '',
                fullWidth: false
            };

            if (firstFullWidthIndex === -1) {
                return { ...prev, specs: [...prev.specs, newSpec] };
            }

            return {
                ...prev,
                specs: [
                    ...prev.specs.slice(0, firstFullWidthIndex),
                    newSpec,
                    ...prev.specs.slice(firstFullWidthIndex)
                ]
            };
        });
    };

    const removeDraftSpec = () => {
        setEditingDraft((prev) => {
            if (!prev) {
                return prev;
            }

            const nonFullSpecs = prev.specs.filter((spec) => !spec.fullWidth);
            const minRows = getMinNormalSpecRowsForType(prev.type);
            if (nonFullSpecs.length <= minRows) {
                showToast(`기본 사양 ${minRows}개는 유지되어야 합니다.`, 'error');
                return prev;
            }

            const removeIndex = [...prev.specs]
                .map((spec, index) => ({ spec, index }))
                .filter((item) => !item.spec.fullWidth)
                .map((item) => item.index)
                .pop();

            if (removeIndex === undefined) {
                return prev;
            }

            return {
                ...prev,
                specs: prev.specs.filter((_, index) => index !== removeIndex),
            };
        });
    };

    const movePage = (page) => {
        if (page < 1 || page > totalPages) {
            return;
        }

        setCurrentPage(page);
        setExpandedModelId(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        writeClientLog({
            actionType: 'list_page_move',
            targetType: 'list_page',
            targetId: page,
            description: `리스트 페이지 이동(${page})`,
        });
    };

    const getTypeLabel = (type) => {
        return type === 'adjuster' ? '조절기' : '제어기';
    };

    const getReviewChangeMap = (model) => {
        const map = new Map();
        if (!showReviewMeta || model?.reviewEventType !== 'update' || !Array.isArray(model?.reviewDiff)) {
            return map;
        }
        model.reviewDiff.forEach((change) => {
            map.set(change.field, change);
        });
        return map;
    };

    const formatReviewChangeText = (change) => (
        `변경전 : ${String(change?.before || '-')} / 변경후 : ${String(change?.after || '-')}`
    );

    return (
        <div
            className={`model-list-section model-list-section--${listVariant}${presentationVariant === 'modelSpecDeck' ? ' spec-list-presentation--modelSpecDeck' : ''}`}
            role="region"
            aria-label={listVariant === 'modelSpecList' ? '모델별 사양 리스트' : '전체 사양 관리 목록'}
        >
            <div className="model-list-head">
                {!omitSectionHeading && (
                    <h3>{listVariant === 'modelSpecList' ? '모델별 사양 리스트' : '사양 관리'}</h3>
                )}
                <div className={`model-list-head-right${omitSectionHeading ? ' model-list-head-right--full' : ''}`}>
                    {headerExtra}
                    {showTypeFilter && (
                        <div className="type-filter-buttons">
                            <button
                                type="button"
                                className={`button type-filter-button adjuster ${typeFilters.adjuster ? '' : 'off'}`}
                                onClick={() => onToggleTypeFilter && onToggleTypeFilter('adjuster')}
                            >
                                조절기
                            </button>
                            <button
                                type="button"
                                className={`button type-filter-button controller ${typeFilters.controller ? '' : 'off'}`}
                                onClick={() => onToggleTypeFilter && onToggleTypeFilter('controller')}
                            >
                                제어기
                            </button>
                        </div>
                    )}
                </div>
            </div>
            {models.length === 0 && (
                <div className="empty-list-message">
                    {listVariant === 'modelSpecList' ? '모델별로 표시할 제품이 없습니다.' : '표시할 제품이 없습니다.'}
                </div>
            )}
            <div className={presentationVariant === 'modelSpecDeck' ? 'model-spec-deck-grid' : 'model-spec-deck-grid--passThrough'}>
            {pagedModels.map((model) => (
                <div
                    id={`model-row-${model.id}`}
                    key={model.id || `${model.siteName}-${model.name}`}
                    className={presentationVariant === 'modelSpecDeck' ? 'model-spec-deck-item' : undefined}
                >
                    <div
                        className={`model-name${!allowPendingExpand ? ' model-name-general' : ''}${listSummaryModelNameOnly && !allowPendingExpand ? ' model-name-summary-model-only' : ''}${presentationVariant === 'modelSpecDeck' ? ' model-spec-deck-row' : ''}${enableRowSelect ? ' model-name-with-selector' : ''}${isMainListDetailLockedForUser(model) ? ' model-name-detail-locked' : ''}`}
                        onClick={() => toggleExpand(model.id)}
                    >
                        {enableRowSelect && (
                            <span className="model-row-selector-wrap">
                                <AppleSwitchCheckbox
                                    checked={selectedRowIds.includes(model.id)}
                                    disabled={typeof isRowSelectable === 'function' ? !isRowSelectable(model) : false}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => onToggleRowSelect && onToggleRowSelect(model, e.target.checked)}
                                    aria-label={listSummaryModelNameOnly
                                        ? `행 선택: ${String(model.name || '').trim()}`
                                        : `행 선택: ${String(model.siteName || '').trim()} / ${String(model.name || '').trim()}`}
                                />
                            </span>
                        )}
                        <span className={`model-type-badge ${getTypeLabel(model.type)}`}>{getTypeLabel(model.type)}</span>
                        {allowPendingExpand && model.reviewStatus === 'pending' && (
                            <>
                                <span className="review-dot" />
                                <span className="review-event-chip">
                                    {model.reviewEventType === 'create'
                                        ? '신규 등록'
                                        : model.reviewEventType === 'delete'
                                            ? '삭제 대기중'
                                            : '기존 수정'}
                                </span>
                            </>
                        )}
                        <div className="model-list-main-cols">
                            {!listSummaryModelNameOnly && (
                                <div
                                    className="model-list-site"
                                    title={editingModelId === model.id
                                        ? `현장명: ${String(editingDraft?.siteName ?? '').trim()}`
                                        : `현장명: ${String(model.siteName ?? '').trim()}`}
                                >
                                    현장명: {editingModelId === model.id ? editingDraft?.siteName : model.siteName}
                                </div>
                            )}
                            <div
                                className={`model-list-product${presentationVariant === 'modelSpecDeck' ? ' model-spec-deck-product' : ''}`}
                                title={editingModelId === model.id
                                    ? `모델명: ${String(editingDraft?.name ?? '').trim()}`
                                    : `모델명: ${String(model.name ?? '').trim()}`}
                            >
                                {listSummaryModelNameOnly && presentationVariant === 'modelSpecDeck' ? (
                                    <span className="model-spec-deck-name-only">{editingModelId === model.id ? editingDraft?.name : model.name}</span>
                                ) : (
                                    <>모델명: {editingModelId === model.id ? editingDraft?.name : model.name}</>
                                )}
                            </div>
                            {!listSummaryModelNameOnly && (
                                <div
                                    className="model-list-household"
                                    title={editingModelId === model.id
                                        ? `세대수: ${String(editingDraft?.householdCount ?? '').trim()}`
                                        : `세대수: ${String(model.householdCount ?? '').trim()}`}
                                >
                                    세대수: {editingModelId === model.id ? editingDraft?.householdCount : (model.householdCount || '-')}
                                </div>
                            )}
                        </div>
                        <div className="model-name-trailing">
                            {showGeneralListReviewBadge(model) && (
                                <span className="model-list-review-plain">
                                    <span className="model-list-review-author">
                                        작성자:{String(model.pendingRequesterName || '').trim() || '-'}
                                    </span>
                                    {getReviewStatusNorm(model) === 'rejected' ? (
                                        <span className="model-list-review-status is-reject">승인 거부됨</span>
                                    ) : getReviewStatusNorm(model) === 'pending' ? (
                                        model.reviewEventType === 'delete' ? (
                                            <span className="model-list-review-status is-pending">삭제 승인 대기중</span>
                                        ) : model.reviewEventType === 'update' ? (
                                            <span className="model-list-review-status is-pending">재승인 검토중</span>
                                        ) : (
                                            <span className="model-list-review-status is-pending">승인 검토중</span>
                                        )
                                    ) : (
                                        <span className="model-list-review-status is-reject">승인 거부됨</span>
                                    )}
                                </span>
                            )}
                            {(canEditProduct || allowPendingExpand) && (
                                <div className="model-actions" onClick={(e) => e.stopPropagation()}>
                                    {canEditProduct && editingModelId === model.id ? (
                                        <>
                                            <button className="button button-add-spec model-edit-button" onClick={addDraftSpec} disabled={isSaving}>사양 추가</button>
                                            <button className="button button-cancel model-edit-button" onClick={removeDraftSpec} disabled={isSaving || isDeleting}>사양 제거</button>
                                            <button className="button model-edit-button" onClick={saveEdit} disabled={isSaving || isDeleting}>{isSaving ? '저장중...' : '저장'}</button>
                                            <button className="button button-cancel model-edit-button" onClick={() => deleteModel(model.id)} disabled={isSaving || isDeleting}>{isDeleting ? '삭제중...' : '삭제'}</button>
                                            <button className="button button-cancel model-edit-button" onClick={cancelEdit} disabled={isSaving || isDeleting}>취소</button>
                                        </>
                                    ) : allowPendingExpand ? (
                                        <>
                                            <button className="button model-edit-button" onClick={() => toggleExpand(model.id)}>
                                                {expandedModelId === model.id ? '상세 닫기' : '상세 보기'}
                                            </button>
                                            {expandedModelId === model.id && typeof onRejectModel === 'function' && (
                                                <button className="button button-cancel model-edit-button" onClick={() => onRejectModel(model.id)}>승인 거부</button>
                                            )}
                                            {expandedModelId === model.id && typeof onApproveModel === 'function' && (
                                                <button className="button model-edit-button" onClick={() => onApproveModel(model.id)}>최종 승인</button>
                                            )}
                                        </>
                                    ) : (
                                        <button
                                            className="button model-edit-button"
                                            onClick={() => startEdit(model)}
                                            disabled={!hasPermissionManagerBypass && isPostRejectAuthorOnlyList(model) && !isOwnerModel(model)}
                                        >
                                            수정
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    {typeof renderCollapsedExtra === 'function' && expandedModelId !== model.id && (
                        <div className="model-collapsed-extra">
                            {renderCollapsedExtra(model)}
                        </div>
                    )}
                    {expandedModelId === model.id && (
                        <div
                            className={presentationVariant === 'modelSpecDeck' ? 'model-spec-deck-expanded' : undefined}
                            style={presentationVariant === 'modelSpecDeck' ? undefined : { display: 'contents' }}
                        >
                            {(() => {
                                const reviewChangeMap = getReviewChangeMap(model);
                                const siteChange = reviewChangeMap.get('siteName');
                                const nameChange = reviewChangeMap.get('productName');
                                if (!showReviewMeta || model.reviewEventType !== 'update' || (!siteChange && !nameChange)) {
                                    return null;
                                }
                                return (
                                    <div className="edit-top-row review-top-row">
                                        {siteChange && (
                                            <div className="spec-input">
                                                <label>
                                                    <span className="spec-changed-dot" />
                                                    현장명
                                                </label>
                                                <div className="review-inline-change">{formatReviewChangeText(siteChange)}</div>
                                            </div>
                                        )}
                                        {nameChange && (
                                            <div className="spec-input">
                                                <label>
                                                    <span className="spec-changed-dot" />
                                                    모델명
                                                </label>
                                                <div className="review-inline-change">{formatReviewChangeText(nameChange)}</div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                            {editingModelId === model.id && (
                                <div className="top-required-row top-required-row-triple spec-edit-top-row">
                                    <div className="form-field top-required-item-40">
                                        <label className="field-label">현장명</label>
                                        <input
                                            className="field-input"
                                            type="text"
                                            value={editingDraft?.siteName || ''}
                                            onChange={(e) => updateDraftField('siteName', e.target.value)}
                                        />
                                    </div>
                                    <div className="form-field top-required-item-40">
                                        <label className="field-label">모델명</label>
                                        <input
                                            className="field-input"
                                            type="text"
                                            value={editingDraft?.name || ''}
                                            onChange={(e) => updateDraftField('name', e.target.value)}
                                        />
                                    </div>
                                    <div className="form-field top-required-item-20">
                                        <label className="field-label">세대수</label>
                                        <input
                                            className="field-input"
                                            type="text"
                                            value={editingDraft?.householdCount || ''}
                                            onChange={(e) => updateDraftField('householdCount', e.target.value)}
                                        />
                                    </div>
                                    <div className="form-field">
                                        <label className="field-label">시/도</label>
                                        <select
                                            className="field-input"
                                            value={editingDraft?.sido || ''}
                                            onChange={(e) => {
                                                updateDraftField('sido', e.target.value);
                                                updateDraftField('gugun', '');
                                            }}
                                        >
                                            <option value="">시/도</option>
                                            {SIDO_OPTIONS.map((item) => (
                                                <option key={item} value={item}>{item}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-field">
                                        <label className="field-label">구/군</label>
                                        <select
                                            className="field-input"
                                            value={editingDraft?.gugun || ''}
                                            onChange={(e) => {
                                                updateDraftField('gugun', e.target.value);
                                            }}
                                            disabled={!editingDraft?.sido}
                                        >
                                            <option value="">구/군</option>
                                            {getGugunOptions(editingDraft?.sido || '').map((item) => (
                                                <option key={item} value={item}>{item}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-field">
                                        <label className="field-label">상세주소</label>
                                        <input
                                            className="field-input"
                                            type="text"
                                            value={editingDraft?.detailAddress || ''}
                                            onChange={(e) => updateDraftField('detailAddress', e.target.value)}
                                            placeholder="지번·도로명 등 상세주소"
                                        />
                                    </div>
                                </div>
                            )}
                            {showReviewMeta && model.reviewStatus === 'pending' && (
                                <div className="review-diff-wrap">
                                    <h4>
                                        {model.reviewEventType === 'create'
                                            ? '신규 등록 항목'
                                            : model.reviewEventType === 'delete'
                                                ? '삭제 대기중 항목'
                                                : '수정전 / 수정후 비교'}
                                    </h4>
                                    {model.reviewEventType === 'update' ? (
                                        Array.isArray(model.reviewDiff) && model.reviewDiff.length > 0 ? (
                                            <ul className="review-diff-list">
                                                {model.reviewDiff.map((change, index) => (
                                                    <li key={`${change.field}-${index}`}>
                                                        <span className="review-diff-label">{change.label}:</span> {formatReviewChangeText(change)}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p>변경 내역 정보가 없습니다.</p>
                                        )
                                    ) : model.reviewEventType === 'delete' ? (
                                        <p>삭제 승인 요청된 리스트입니다. 최종 승인 시 실제로 삭제됩니다.</p>
                                    ) : (
                                        <p>신규 생성된 리스트입니다.</p>
                                    )}
                                </div>
                            )}
                            {showReviewMeta && (
                                <div className="review-request-type-text">
                                    요청 유형: {model.reviewEventType === 'create' ? '리스트 등록' : model.reviewEventType === 'delete' ? '리스트 삭제' : '리스트 수정'}
                                </div>
                            )}
                            <div className={editingModelId === model.id ? 'form-fields-grid spec-edit-fields-grid' : 'spec-view-grid'}>
                            {(editingModelId === model.id ? editingDraft?.specs || [] : model.specs).map((spec, specIndex) => (
                                <React.Fragment key={specIndex}>
                                <div
                                    className={editingModelId === model.id
                                        ? `form-field ${spec.fullWidth ? 'full-width' : ''}`
                                        : `spec-view-item ${spec.fullWidth ? 'full-width' : ''}`}
                                >
                                    <label className={editingModelId === model.id ? 'field-label' : ''}>
                                        {showReviewMeta && model.reviewEventType === 'update' && Array.isArray(model.reviewDiff) && model.reviewDiff.some((change) => change.field === `spec:${spec.title}`) && (
                                            <span className="spec-changed-dot" />
                                        )}
                                        {spec.title}
                                    </label>
                                    {isUploadSpecTitle(spec.title) ? (
                                        <div className={`spec-file-view ${editingModelId === model.id ? '' : 'spec-view-file-box'}`}>
                                            {(() => {
                                                const fileInfos = parseSpecFileListValue(spec.details);
                                                return (
                                                    <>
                                                        {fileInfos.some((file) => file.kind === 'image') && (
                                                            <div className="spec-image-grid">
                                                                {fileInfos.map((file, idx) => ({ file, idx }))
                                                                    .filter((item) => item.file.kind === 'image')
                                                                    .map(({ file, idx }) => (
                                                                    <div key={`${file.name}-${idx}`} className="spec-image-item">
                                                                        <img
                                                                            src={file.dataUrl}
                                                                            alt={file.name || spec.title}
                                                                            className="spec-upload-preview"
                                                                            onClick={() => setLightboxImage({ src: file.dataUrl, alt: file.name || spec.title })}
                                                                        />
                                                                        {editingModelId === model.id && (
                                                                            <button
                                                                                type="button"
                                                                                className="spec-image-delete-button"
                                                                                onClick={() => clearDraftSpecFile(specIndex, idx)}
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {isFirmwareSpecTitle(spec.title) && fileInfos.filter((file) => file.kind === 'firmware').map((file, idx) => (
                                                            <div key={`${file.name}-${idx}`} className="spec-firmware-row-wrap">
                                                                <div className="spec-firmware-row">
                                                                    <span className="spec-firmware-name">{file.name}</span>
                                                                    {canDownloadFirmware && (
                                                                        <a
                                                                            className="button spec-download-button"
                                                                            href={file.dataUrl}
                                                                            download={file.name}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                        >
                                                                            다운로드
                                                                        </a>
                                                                    )}
                                                                </div>
                                                                {file.description && (
                                                                    <div className="spec-firmware-tooltip">
                                                                        {file.description}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                        {isWiringDiagramSpecTitle(spec.title) && fileInfos.filter((file) => file.kind === 'wiringPdf').map((file, idx) => (
                                                            <div key={`wiring-${file.name}-${idx}`} className="spec-firmware-row-wrap">
                                                                <div className="spec-firmware-row">
                                                                    <span className="spec-firmware-name">{file.name}</span>
                                                                    {canDownloadWiringDiagram && (
                                                                        <a
                                                                            className="button spec-download-button"
                                                                            href={file.dataUrl}
                                                                            download={file.name}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                        >
                                                                            다운로드
                                                                        </a>
                                                                    )}
                                                                </div>
                                                                {file.description && (
                                                                    <div className="spec-firmware-tooltip">
                                                                        {file.description}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                        {!fileInfos.some((file) => file.kind === 'image')
                                                            && !fileInfos.some((file) => file.kind === 'firmware')
                                                            && !fileInfos.some((file) => file.kind === 'wiringPdf') && (
                                                            <span className="spec-file-empty">업로드된 파일 없음</span>
                                                        )}
                                                        {showReviewMeta && model.reviewEventType === 'update' && (() => {
                                                            const change = (model.reviewDiff || []).find((item) => item.field === `spec:${spec.title}`);
                                                            if (!change) {
                                                                return null;
                                                            }
                                                            return (
                                                                <div className="review-inline-change">
                                                                    {formatReviewChangeText(change)}
                                                                </div>
                                                            );
                                                        })()}
                                                    </>
                                                );
                                            })()}
                                            {editingModelId === model.id && (
                                                <div className="spec-file-actions">
                                                    <input
                                                        type="file"
                                                        accept={
                                                            isImageSpecTitle(spec.title)
                                                                ? 'image/*'
                                                                : isFirmwareSpecTitle(spec.title)
                                                                    ? '.hex'
                                                                    : '.pdf,application/pdf'
                                                        }
                                                        multiple
                                                        onChange={updateDraftSpecFile(
                                                            specIndex,
                                                            isImageSpecTitle(spec.title)
                                                                ? 'image'
                                                                : isFirmwareSpecTitle(spec.title)
                                                                    ? 'firmware'
                                                                    : 'wiringPdf'
                                                        )}
                                                    />
                                                </div>
                                            )}
                                            {editingModelId === model.id && (isFirmwareSpecTitle(spec.title) || isWiringDiagramSpecTitle(spec.title)) && (() => {
                                                const parsedFiles = parseSpecFileListValue((editingDraft?.specs || [])[specIndex]?.details);
                                                const rows = parsedFiles
                                                    .map((file, fileIndex) => ({ file, fileIndex }))
                                                    .filter(({ file }) => {
                                                        if (isFirmwareSpecTitle(spec.title)) {
                                                            return file.kind === 'firmware';
                                                        }
                                                        if (isWiringDiagramSpecTitle(spec.title)) {
                                                            return file.kind === 'wiringPdf';
                                                        }
                                                        return false;
                                                    });
                                                if (rows.length === 0) {
                                                    return null;
                                                }
                                                return rows.map(({ file, fileIndex }) => (
                                                    <div key={`${file.name}-${fileIndex}`} className="firmware-edit-row">
                                                        <div className="file-selected-row">
                                                            <span className="file-selected-name">{file.name}</span>
                                                            <button type="button" className="button button-cancel model-edit-button" onClick={() => clearDraftSpecFile(specIndex, fileIndex)}>삭제</button>
                                                        </div>
                                                        <textarea
                                                            className="field-input firmware-description-input"
                                                            value={file.description || ''}
                                                            onChange={(e) => updateDraftUploadFileDescription(specIndex, fileIndex, e.target.value)}
                                                            placeholder={
                                                                isWiringDiagramSpecTitle(spec.title)
                                                                    ? '결선도 파일 메모를 입력해주세요.'
                                                                    : '펌웨어 파일 설명을 입력해주세요.'
                                                            }
                                                        />
                                                    </div>
                                                ));
                                            })()}
                                        </div>
                                    ) : editingModelId === model.id ? (
                                        <>
                                            <SpecDropdownSelect
                                                className="field-input"
                                                specTitle={spec.title}
                                                value={spec.details}
                                                onChange={(v) => updateDraftSpec(specIndex, v)}
                                                options={specDropdownOptions}
                                                disabled={false}
                                            />
                                            {showReviewMeta && model.reviewEventType === 'update' && (() => {
                                                const change = (model.reviewDiff || []).find((item) => item.field === `spec:${spec.title}`);
                                                if (!change) {
                                                    return null;
                                                }
                                                return (
                                                    <div className="review-inline-change">
                                                        {formatReviewChangeText(change)}
                                                    </div>
                                                );
                                            })()}
                                        </>
                                    ) : (
                                        <div className="spec-view-value">{spec.details || '-'}</div>
                                    )}
                                </div>
                                {String(spec.title).trim() === '비고' && showRejectDetailUnderMemo(model) && (
                                    <div
                                        className={
                                            editingModelId === model.id
                                                ? 'form-field full-width spec-reject-under-memo'
                                                : 'spec-view-item full-width spec-reject-under-memo'
                                        }
                                    >
                                        <div className="spec-view-value spec-reject-history-inline">
                                            {formatRejectHistoryInlineText(model) || '등록된 승인 거부 내용이 없습니다.'}
                                        </div>
                                    </div>
                                )}
                                </React.Fragment>
                            ))}
                            {showRejectDetailUnderMemo(model)
                                && !(editingModelId === model.id ? editingDraft?.specs || [] : model.specs).some((s) => String(s.title || '').trim() === '비고') && (
                                <div
                                    className={
                                        editingModelId === model.id
                                            ? 'form-field full-width spec-reject-under-memo'
                                            : 'spec-view-item full-width spec-reject-under-memo'
                                    }
                                >
                                    <div className="spec-view-value spec-reject-history-inline">
                                        {formatRejectHistoryInlineText(model) || '등록된 승인 거부 내용이 없습니다.'}
                                    </div>
                                </div>
                            )}
                        </div>
                        </div>
                    )}
                </div>
            ))}
            </div>

            {totalPages > 1 && (
                <div className="pagination-wrap">
                    <button className="pagination-button" onClick={() => movePage(currentPage - 1)} disabled={currentPage === 1}>이전</button>
                    {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                        <button
                            key={page}
                            className={`pagination-button ${page === currentPage ? 'active' : ''}`}
                            onClick={() => movePage(page)}
                        >
                            {page}
                        </button>
                    ))}
                    <button className="pagination-button" onClick={() => movePage(currentPage + 1)} disabled={currentPage === totalPages}>다음</button>
                </div>
            )}

            {toastMessage && (
                <div className={`toast-popup ${toastVisible ? 'show' : ''} ${toastType === 'success' ? 'toast-success' : 'toast-error'}`}>{toastMessage}</div>
            )}
            {lightboxImage && (
                <div className="lightbox-backdrop" onClick={() => setLightboxImage(null)}>
                    <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <button className="lightbox-close" onClick={() => setLightboxImage(null)}>×</button>
                        <img src={lightboxImage.src} alt={lightboxImage.alt} className="lightbox-image" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SpecList;